import React, { useEffect, useRef, useCallback } from 'react';
import './KycWaiting.css';
import { apiUrl } from '../config/envConfig';
import { getEnvironmentParams } from '../utils/auth';
import PageLayout from './common/PageLayout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function KycWaiting() {
    const navigate = useNavigate();
    const pollIntervalRef = useRef(null);
    const kycUrlRef = useRef(null);
    const hasInitiated = useRef(false);
    const { t } = useTranslation();


    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    const checkKYCStatus = useCallback(async () => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) return null;

            const response = await fetch(`${apiUrl}/kycs/status`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${params.token}`
                }
            });

            if (!response.ok) return null;

            const data = await response.json();
            return data.kycStatus || null;
        } catch (error) {
            console.error('KYC status check error:', error);
            return null;
        }
    }, []);

    const initiateKYC = useCallback(async () => {
        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                navigate('/kyc-complete', { state: { failed: true } });
                return;
            }

            const response = await fetch(`${apiUrl}/kycs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${params.token}`
                }
            });

            if (!response.ok) {
                navigate('/kyc-complete', { state: { failed: true } });
                return;
            }

            const data = await response.json();

            if (data) {
                kycUrlRef.current = data;
                window.open(data, '_blank', 'width=1000,height=1000,resizable=yes');


                // Start polling
                pollIntervalRef.current = setInterval(async () => {
                    const status = await checkKYCStatus();
                    if (status === 'Valid') {
                        stopPolling();
                        //navigate('/kyc-complete');
                    } else if (status === 'Failed' || status === 'Rejected') {
                        stopPolling();
                        //navigate('/kyc-complete', { state: { failed: true } });
                    }
                }, 30000);
            } else {
                navigate('/kyc-complete', { state: { failed: true } });
            }
        } catch (error) {
            console.error('KYC initiation error:', error);
            navigate('/kyc-complete', { state: { failed: true } });
        }
    }, [navigate, checkKYCStatus, stopPolling]);

    useEffect(() => {
        if (hasInitiated.current) return;
        hasInitiated.current = true;

        const init = async () => {
            // First check if KYC is already valid
            const status = await checkKYCStatus();
            if (status === 'Valid') {
                navigate('/kyc-complete');
                return;
            }
            if (status === 'Failed' || status === 'Rejected') {
                navigate('/kyc-complete', { state: { failed: true } });
                return;
            }

            // Not valid yet — initiate KYC and open popup
            await initiateKYC();
        };

        init();

        return () => stopPolling();
    }, [checkKYCStatus, initiateKYC, navigate, stopPolling]);

    const handleReopenWindow = () => {
        if (kycUrlRef.current) {
            window.open(kycUrlRef.current, '_blank', 'width=1000,height=1000,resizable=yes');
        }
    };

    const navigationProps = {
        showPrevious: false,
        showNext: false,
        align: 'center'
    };

    return (
        <PageLayout currentStep={1} navigationProps={navigationProps}>
            <div className="kyc-waiting-content">
                <div className="kyc-waiting-spinner" />

                <h2 className="kyc-waiting-title">{t('kycWaitingTitle')}</h2>
                <p className="kyc-waiting-desc">{t('kycWaitingDesc')}</p>

                <div className="kyc-waiting-recovery">
                    <div className="kyc-recovery-title">{t('kycWaitingRecoveryTitle')}</div>
                    <p className="kyc-recovery-desc">{t('kycWaitingRecoveryDesc')}</p>
                    <button className="kyc-reopen-btn" onClick={handleReopenWindow}>
                        {t('kycWaitingReopenBtn')}
                    </button>
                </div>
            </div>
        </PageLayout>
    );
}

export default KycWaiting;
